import React from "react";
import Select from "react-select";

import { translate } from "../IntlProviderWrapper";
import { classNames } from "../../lib/Util";

import "../../styles/pagination.scss";

export type Props = {
    can_previous_page: boolean;
    can_next_page: boolean;
    class_name?: string;
    num_rows: number;
    page_count: number;
    page_options: number[];
    page_index: number;
    page_size: number;
    page_sizes?: number[];
    gotoPage: (page: number) => void;
    nextPage: () => void;
    previousPage: () => void;
    setPageSize: (page_size: number) => void;
    onPageChange?: (page_index: number) => void;
    onPageSizeChange?: (page_size: number) => void;
};

const Pagination = ({
    num_rows,
    can_previous_page,
    can_next_page,
    page_options,
    page_count,
    page_index,
    page_size = 25,
    page_sizes = [10, 25, 50, 100],
    class_name = "",
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    onPageChange = () => {},
    onPageSizeChange = () => {},
}: Props) => {
    const row_start = num_rows > 0 ? page_index * page_size + 1 : 0;
    const row_end = Math.min((page_index + 1) * page_size, num_rows);

    return (
        <div className={classNames("pagination", { [String(class_name)]: class_name !== "" })}>
            <span className="pagination__buttons">
                <button
                    className="btn btn-flat-light btn-short"
                    title={translate("Pagination.first_page", "First page")}
                    onClick={() => {
                        gotoPage(0);
                        onPageChange(0);
                    }}
                    disabled={!can_previous_page}
                >
                    <i className="fas fa-angle-double-left" />
                </button>
                <button
                    className="btn btn-flat-light btn-short"
                    title={translate("Pagination.previous_page", "Previous page")}
                    onClick={() => {
                        previousPage();
                        onPageChange(page_index - 1);
                    }}
                    disabled={!can_previous_page}
                >
                    <i className="fas fa-angle-left" />
                </button>
                <button
                    className="btn btn-flat-light btn-short"
                    title={translate("Pagination.next_page", "Next page")}
                    onClick={() => {
                        nextPage();
                        onPageChange(page_index + 1);
                    }}
                    disabled={!can_next_page}
                >
                    <i className="fas fa-angle-right" />
                </button>
                <button
                    className="btn btn-flat-light btn-short"
                    title={translate("Pagination.last_page", "Last page")}
                    onClick={() => {
                        gotoPage(page_count - 1);
                        onPageChange(page_count - 1);
                    }}
                    disabled={!can_next_page}
                >
                    <i className="fas fa-angle-double-right" />
                </button>
            </span>
            {page_count !== 1 && page_index >= 0 && (
                <span className="pagination__page">
                    <label>
                        <span>{translate("common.page", "Page")}:</span>
                        {page_options.length > 0 ? (
                            <Select
                                className="d-inline-block"
                                menuPlacement="auto"
                                menuPosition="fixed"
                                components={{
                                    IndicatorSeparator: null,
                                }}
                                options={page_options.map(i => ({
                                    label: i + 1,
                                    value: i,
                                }))}
                                value={{
                                    label: page_index + 1,
                                    value: page_index,
                                }}
                                theme={theme => ({
                                    ...theme,
                                    spacing: {
                                        ...theme.spacing,
                                        baseUnit: 2,
                                        controlHeight: 30,
                                    },
                                })}
                                styles={{
                                    control: base => ({ ...base, minWidth: 70 }),
                                }}
                                onChange={option => {
                                    gotoPage(option.value);
                                    onPageChange(option.value);
                                }}
                            />
                        ) : (
                            <span>
                                {page_count > 1 ? (
                                    <input
                                        className="form-control form-control-sm d-inline w-auto"
                                        type="number"
                                        value={Math.max(1, Math.min(page_count, page_index + 1))}
                                        onChange={(event: React.SyntheticEvent<HTMLInputElement>) => {
                                            const new_page_index =
                                                Math.max(1, Math.min(page_count, Number(event.currentTarget.value))) -
                                                1;
                                            if (new_page_index !== page_index) {
                                                gotoPage(new_page_index);
                                                onPageChange(new_page_index);
                                            }
                                        }}
                                        min={1}
                                        max={page_count}
                                    />
                                ) : (
                                    page_index + 1
                                )}
                                {page_count > 1 && (
                                    <>
                                        {" "}
                                        {translate("common.of", "of")} {page_count}
                                    </>
                                )}
                            </span>
                        )}
                    </label>
                </span>
            )}
            {num_rows > 0 && (
                <span className="pagination__rows">
                    <label>
                        <span>{translate("common.rows_per_page", "Rows per page")}:</span>
                        <Select
                            className="d-inline-block"
                            menuPlacement="auto"
                            menuPosition="fixed"
                            components={{
                                IndicatorSeparator: null,
                            }}
                            options={page_sizes.map(size => ({
                                label: size,
                                value: size,
                            }))}
                            value={{
                                label: page_size,
                                value: page_size,
                            }}
                            theme={theme => ({
                                ...theme,
                                spacing: {
                                    ...theme.spacing,
                                    baseUnit: 2,
                                    controlHeight: 30,
                                },
                            })}
                            styles={{
                                control: base => ({ ...base, minWidth: 70 }),
                            }}
                            onChange={option => {
                                setPageSize(option.value);
                                onPageSizeChange(option.value);
                            }}
                        />
                    </label>
                </span>
            )}
            {num_rows > 0 && (
                <span className="pagination__status">
                    {row_start} - {row_end} {translate("common.of", "of")} {num_rows}
                </span>
            )}
        </div>
    );
};

export default Pagination;
